import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { ApiExternalUserProfile } from '../api/userProfile/userProfileModels';

import userProfileFlow from '../flow/userProfileFlow';
import persistance from '../persistance/persistance';
import { featureFlagsSelectors } from '../selectors/featureFlagsSelectors';
import { ErrorGroup } from '../store/structureDefinitions/errorsState';
import { useGroupError } from './useGroupError';
import { useAppSelector } from './useThunkDispatch';
import { useIsUserAuthenticated, useShoppingUserId } from './useUserProfile';

const userProfileQueryKeys = {
    all: ['UserProfile'] as const,
    byUserId: (userId: string) => [...userProfileQueryKeys.all, userId],
};

const ensureProfile = async () => {
    let profile = await userProfileFlow.getProfile();

    if (!profile) {
        profile = await userProfileFlow.createDefaultProfile();
    }

    persistance.userProfileSession.setProfile(profile.userId, profile);

    return profile;
};

const profileQueryOptions = (userId: string) => {
    return queryOptions<ApiExternalUserProfile>({
        queryKey: userProfileQueryKeys.byUserId(userId),
        queryFn: ensureProfile,
        retry: false,
        staleTime: 1000 * 10 * 60, // 10 minutes
    });
};

export const useProfileQuery = () => {
    const isAuthenticated = useIsUserAuthenticated();
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';
    const isProfileEnabled = useAppSelector((state) =>
        featureFlagsSelectors.selectDynamicFlagEnabled(state, 'EnableUserProfile'),
    );

    const getProfileQuery = useQuery({
        ...profileQueryOptions(shoppingUserId),
        enabled: isAuthenticated && !!shoppingUserId && isProfileEnabled,
        placeholderData: () => persistance.userProfileSession.getProfile(shoppingUserId), // use what we have in session and then update
    });

    return getProfileQuery;
};

export const useProfileMutations = () => {
    const queryClient = useQueryClient();
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';

    const ensureProfileExists = useCallback(async () => {
        const profile = await queryClient.ensureQueryData<ApiExternalUserProfile>({
            ...profileQueryOptions(shoppingUserId),
            // does not revalidate even if stale, actually this is good for us since profile once done cannot be removed
            // will only get it if it was not in session storage
            // but leaving the flag it here in case of problems
            // revalidateIfStale: true,
        });

        return profile;
    }, [queryClient, shoppingUserId]);

    const createDefaultProfile = useMutation({
        mutationFn: async () => {
            return userProfileFlow.createDefaultProfile();
        },
    });

    const setPriceTypeMutation = useMutation({
        mutationFn: async (priceType: 'ExcludeTax' | 'IncludeTax') => {
            await ensureProfileExists();
            return userProfileFlow.updateProfile({
                priceType,
            });
        },
        onSuccess: async (profile) => {
            queryClient.setQueryData(userProfileQueryKeys.byUserId(shoppingUserId), profile);
            persistance.userProfileSession.setProfile(profile.userId, profile);
        },
    });

    useGroupError(ErrorGroup.Global, setPriceTypeMutation.error);
    useGroupError(ErrorGroup.Global, createDefaultProfile.error);

    return {
        ensureProfileExists,
        createDefaultProfile,
        setPriceTypeMutation,
    };
};
